import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Container, Heading, Text, Button, HStack, VStack, Badge, Flex, Image, Icon, Progress,
} from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight, FiPlay, FiPause, FiMonitor, FiSmartphone, FiZap } from 'react-icons/fi';
import { TOUR_TRACKS, SECONDS_PER_SLIDE } from './tourSlides';

const PhoneFrame = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return (
    <Flex justify="center" h="100%" align="center">
      <Box
        w={{ base: '220px', md: '260px' }}
        borderRadius="36px"
        border="10px solid"
        borderColor="gray.700"
        bg="gray.700"
        boxShadow="2xl"
        overflow="hidden"
      >
        {failed ? (
          <Flex h={{ base: '440px', md: '540px' }} align="center" justify="center" bg="gray.50" color="gray.400" px={4} textAlign="center" borderRadius="26px">
            <Text fontSize="sm">{alt}</Text>
          </Flex>
        ) : (
          <Image src={src} alt={alt} w="100%" display="block" borderRadius="26px" loading="lazy" onError={() => setFailed(true)} />
        )}
      </Box>
    </Flex>
  );
};

const BrowserFrame = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return (
    <Box borderRadius="xl" overflow="hidden" boxShadow="2xl" border="1px solid" borderColor="gray.200" bg="white">
      <HStack spacing={1.5} px={3} py={2} bg="gray.100" borderBottom="1px solid" borderColor="gray.200">
        <Box w="10px" h="10px" borderRadius="full" bg="red.300" />
        <Box w="10px" h="10px" borderRadius="full" bg="yellow.300" />
        <Box w="10px" h="10px" borderRadius="full" bg="green.300" />
      </HStack>
      {failed ? (
        <Flex h={{ base: '220px', md: '420px' }} align="center" justify="center" bg="gray.50" color="gray.400" px={6} textAlign="center">
          <Text fontSize="sm">{alt}</Text>
        </Flex>
      ) : (
        <Image src={src} alt={alt} w="100%" display="block" loading="lazy" onError={() => setFailed(true)} />
      )}
    </Box>
  );
};

const ProductTour = () => {
  const [trackKey, setTrackKey] = useState(TOUR_TRACKS[0].key);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);

  const allSlides = useMemo(() => TOUR_TRACKS.flatMap((t) => t.slides.map((s) => ({ ...s, track: t.key }))), []);
  const track = TOUR_TRACKS.find((t) => t.key === trackKey);
  const slide = track.slides[index];
  const globalIndex = allSlides.findIndex((s) => s.image === slide.image);
  const totalMinutes = Math.round((allSlides.length * SECONDS_PER_SLIDE) / 60);

  const goGlobal = useCallback((gi) => {
    const wrapped = (gi + allSlides.length) % allSlides.length;
    const target = allSlides[wrapped];
    const t = TOUR_TRACKS.find((x) => x.key === target.track);
    setTrackKey(t.key);
    setIndex(t.slides.findIndex((s) => s.image === target.image));
  }, [allSlides]);

  const next = useCallback(() => goGlobal(globalIndex + 1), [goGlobal, globalIndex]);
  const prev = useCallback(() => goGlobal(globalIndex - 1), [goGlobal, globalIndex]);

  useEffect(() => {
    if (!playing) return undefined;
    const id = setTimeout(next, SECONDS_PER_SLIDE * 1000);
    return () => clearTimeout(id);
  }, [playing, next]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
  };

  const selectTrack = (key) => {
    setTrackKey(key);
    setIndex(0);
  };

  return (
    <Box id="tour" py={{ base: 16, md: 20 }} bg="gray.900" color="white">
      <Container maxW="1200px">
        <VStack spacing={4} textAlign="center" mb={10}>
          <Badge colorScheme="green" px={3} py={1} borderRadius="full">See the screens · about {totalMinutes} minutes</Badge>
          <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }}>Look at the screens before you switch.</Heading>
          <Text color="gray.300" maxW="720px" fontSize="lg">
            Real screens from a working laundry. One set is what staff see at the counter. The other is what customers see when they book.
            Click through, or press play.
          </Text>
        </VStack>

        <HStack justify="center" spacing={3} mb={8} flexWrap="wrap">
          {TOUR_TRACKS.map((t) => (
            <Button
              key={t.key}
              onClick={() => selectTrack(t.key)}
              leftIcon={<Icon as={t.key === 'pos' ? FiMonitor : FiSmartphone} />}
              borderRadius="full"
              size="md"
              colorScheme={t.key === trackKey ? 'green' : 'whiteAlpha'}
              variant={t.key === trackKey ? 'solid' : 'outline'}
            >
              {t.label} ({t.slides.length})
            </Button>
          ))}
        </HStack>

        <Flex
          direction={{ base: 'column', lg: 'row' }}
          gap={{ base: 6, lg: 10 }}
          align="stretch"
          tabIndex={0}
          onKeyDown={onKeyDown}
          outline="none"
          aria-roledescription="carousel"
          aria-label="Product tour"
        >
          <Box flex={{ lg: 3 }} minW={0}>
            {slide.device === 'phone'
              ? <PhoneFrame src={slide.image} alt={slide.title} />
              : <BrowserFrame src={slide.image} alt={slide.title} />}
          </Box>

          <Flex flex={{ lg: 2 }} direction="column" justify="space-between" minW={0}>
            <Box aria-live="polite">
              <Text fontSize="sm" color="green.300" fontWeight="700" mb={2}>
                Step {globalIndex + 1} of {allSlides.length} · {track.label}
              </Text>
              <Heading as="h3" fontSize={{ base: 'xl', md: '2xl' }} mb={4}>{slide.title}</Heading>
              <Text color="gray.300" fontSize="md" mb={5}>{slide.caption}</Text>
              {slide.unique && (
                <HStack align="flex-start" spacing={3} bg="whiteAlpha.100" borderRadius="lg" p={4} mb={3}>
                  <Icon as={FiZap} color="yellow.300" boxSize={5} mt={0.5} flexShrink={0} />
                  <Text fontSize="sm" color="gray.100"><strong>Worth knowing:</strong> {slide.unique}</Text>
                </HStack>
              )}
              {slide.illustrative && (
                <Text fontSize="xs" color="gray.500">Sample orders, not a live customer.</Text>
              )}
            </Box>

            <Box mt={6}>
              <Progress value={((globalIndex + 1) / allSlides.length) * 100} size="xs" colorScheme="green" bg="whiteAlpha.200" borderRadius="full" mb={4} />
              <HStack spacing={3}>
                <Button onClick={prev} leftIcon={<FiChevronLeft />} variant="outline" colorScheme="whiteAlpha" borderRadius="full">Back</Button>
                <Button onClick={next} rightIcon={<FiChevronRight />} colorScheme="green" borderRadius="full">Next</Button>
                <Button
                  onClick={() => setPlaying((p) => !p)}
                  leftIcon={playing ? <FiPause /> : <FiPlay />}
                  variant="ghost"
                  colorScheme="whiteAlpha"
                  borderRadius="full"
                >
                  {playing ? 'Pause' : 'Play'}
                </Button>
              </HStack>
            </Box>
          </Flex>
        </Flex>

        <HStack spacing={3} mt={8} overflowX="auto" pb={2} justify={{ base: 'flex-start', md: 'center' }}>
          {track.slides.map((s, i) => (
            <Box
              as="button"
              key={s.image}
              onClick={() => setIndex(i)}
              flexShrink={0}
              w={{ base: '96px', md: '120px' }}
              borderRadius="md"
              overflow="hidden"
              border="2px solid"
              borderColor={i === index ? 'green.300' : 'transparent'}
              opacity={i === index ? 1 : 0.55}
              _hover={{ opacity: 1 }}
              transition="all 0.2s"
              aria-label={`Go to ${s.title}`}
            >
              <Image src={s.image} alt="" w="100%" h={{ base: '56px', md: '70px' }} objectFit="cover" objectPosition="top" loading="lazy" />
            </Box>
          ))}
        </HStack>

        <VStack spacing={3} mt={12}>
          <Text color="gray.300" fontSize="lg" textAlign="center">Everything on these screens is included. Free until sales pass $1,000 in a month, then $49 a month, and never more than $149.</Text>
          <Button as="a" href="/quickstart" size="lg" colorScheme="green" borderRadius="full" px={10} rightIcon={<FiChevronRight />}>
            Set Up My Laundry — Free
          </Button>
          <Text color="yellow.300" fontSize="sm" fontWeight="700">⚡ Live in under 2 minutes. No sales calls.</Text>
        </VStack>
      </Container>
    </Box>
  );
};

export default ProductTour;
